import { useEffect, useMemo, useState } from 'react';
import { generateHashEmail } from '../lib/generateHashEmail';

export type UseEmailAllowlistOptions = {
  email?: string | null;
  allowList: string;
  salt: string;
};

export const toSet = (allowList: string) => {
  const allowListArray = allowList.split(',');

  return new Set(allowListArray.map((valueHash: string) => (valueHash ?? '').trim()));
};

/**
 * Extracts domain from email and creates wildcard pattern.
 * Example: "john.doe@openx.com" -> "*@openx.com"
 */
export const getWildcardPattern = (email: string): string | null => {
  const atIndex = email.lastIndexOf('@');
  if (atIndex === -1) return null;
  const domain = email.substring(atIndex); // includes @
  return `*${domain}`;
};

/**
 *  @deprecated
 *  usePermissionContext().isGrowthbookFeatureOn instead.
 */
export const useEmailAllowlist = ({ email, allowList, salt }: UseEmailAllowlistOptions) => {
  const [hash, setHash] = useState<string>('');
  const [wildcardHash, setWildcardHash] = useState<string>('');
  const [ready, setReady] = useState(false);

  const set = useMemo(() => toSet(allowList), [allowList]);

  useEffect(() => {
    let cancelled = false;

    (() => {
      setReady(false);
      if (!email) {
        setHash('');
        setWildcardHash('');
        setReady(true);
        return;
      }
      try {
        // Generate hash for exact email
        const hashEmail: string = generateHashEmail(email, salt);

        // Generate hash for wildcard pattern
        const wildcardPattern = getWildcardPattern(email);
        const hashWildcard = wildcardPattern ? generateHashEmail(wildcardPattern, salt) : '';

        if (!cancelled) {
          setHash(hashEmail);
          setWildcardHash(hashWildcard);
        }
      } catch {
        if (!cancelled) {
          setHash('');
          setWildcardHash('');
        }
      } finally {
        if (!cancelled) setReady(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [email, salt]);

  const inAllowList = useMemo(() => {
    // Check both: exact email hash OR wildcard hash
    if (hash && set.has(hash)) return true;
    if (wildcardHash && set.has(wildcardHash)) return true;
    return false;
  }, [set, hash, wildcardHash]);

  return { ready, hash, inAllowList, count: set.size };
};
